<template>
	<div class="status-cell" :class="{ 'has-date': showPaidDate, 'is-open': isEditorOpen }" ref="containerRef" @click.stop>
		<button
			ref="badgeRef"
			type="button"
			class="status-trigger"
			@click.stop="handleBadgeClick"
			:disabled="loading || disabled"
			aria-haspopup="dialog"
			:aria-expanded="isEditorOpen"
		>
			<span class="status-badge" :class="badgeClass">{{ statusLabel }}</span>
			<span v-if="showPaidDate" class="status-meta">{{ formattedPaidDate }}</span>
		</button>

		<Teleport to="body" :disabled="!useFixed">
			<transition name="status-popover">
				<div v-if="isEditorOpen" ref="popoverRef" class="status-popover" :style="useFixed ? popoverStyle : undefined" role="dialog" aria-label="Invoice status" @click.stop @keydown.esc.stop="cancel">
					<div v-if="isToggleMode" class="toggle-row">
						<label class="toggle-label">Paid</label>
						<label class="toggle-switch">
							<input
								type="checkbox"
								v-model="isPaidToggled"
								aria-label="Paid"
							@change="handleToggleChange"
						/>
							<span class="toggle-slider"></span>
						</label>
					</div>

					<div v-else class="status-select-row">
						<label :for="selectInputId" class="toggle-label">Status</label>
						<select
							:id="selectInputId"
							v-model="selectedStatus"
							class="status-select-input"
							@change="handleStatusChange"
						>
							<option
								v-for="option in normalizedOptions"
								:key="option.value"
								:value="option.value"
							>
								{{ option.label }}
							</option>
						</select>
					</div>

					<div v-if="isPaidSelected" class="date-picker">
						<DateRangePicker
							mode="single"
							use-fixed
							:date-from="paidDateInput"
							:max-date="today"
							:disabled="loading || disabled"
							trigger-label="Paid date"
							@update="({ dateFrom }) => paidDateInput = dateFrom"
						>
							<template #trigger-icon><AppointmentIcon name="calendar" :size="16" /></template>
							<template #previous-icon><AppointmentIcon name="chevron-left" :size="16" /></template>
							<template #next-icon><AppointmentIcon name="chevron-right" :size="16" /></template>
						</DateRangePicker>
				</div>

				<div v-if="showActions" class="popover-actions">
					<button
						type="button"
						class="btn-cancel"
						@click="cancel"
						:disabled="loading"
					>
						Cancel
					</button>

					<button
						type="button"
						class="btn-confirm"
						@click="confirm"
						:disabled="!canConfirm || loading"
					>
						Confirm
					</button>
				</div>
			</div>
		</transition>
		</Teleport>
	</div>
</template>

<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount, onDeactivated } from 'vue';
import { onEvent, emitEvent } from '@/utils/eventBus';
import { useFloatingPanel } from '@/composables/useFloatingPanel';
import DateRangePicker from '@/components/DateRangePicker.vue';
import AppointmentIcon from '@/components/icons/AppointmentIcon.vue';

const DEFAULT_STATUS_OPTIONS = [
	{ value: 'unpaid', label: 'Unpaid' },
	{ value: 'paid', label: 'Paid' },
];

const normalizeStatusValue = (value) =>
	String(value === null || value === undefined ? '' : value)
		.trim()
		.toLowerCase();

const normalizeDateInput = (value) => {
	if (!value) return '';
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return '';
	return date.toISOString().slice(0, 10);
};

const props = defineProps({
	useFixed: Boolean,
	itemId: {
		type: [String, Number],
		required: true,
	},
	status: {
		type: String,
		default: 'unpaid',
	},
	paidDate: {
		type: String,
		default: null,
	},
	statusOptions: {
		type: Array,
		default: () => [
			{ value: 'unpaid', label: 'Unpaid' },
			{ value: 'paid', label: 'Paid' },
		],
	},
	paidStatusValue: {
		type: String,
		default: 'paid',
	},
	disabled: {
		type: Boolean,
		default: false,
	},
	loading: {
		type: Boolean,
		default: false,
	},
});

const emit = defineEmits(['submit', 'open', 'close']);

const containerRef = ref(null);
const isEditorOpen = ref(false);
const badgeRef = ref(null);
const popoverRef = ref(null);
const popoverStyle = useFloatingPanel({
	open: isEditorOpen, anchor: badgeRef, panel: popoverRef, enabled: () => props.useFixed,
});
const selectedStatus = ref('');
const paidDateInput = ref('');

const normalizedOptions = computed(() => {
	const source = Array.isArray(props.statusOptions) ? props.statusOptions : [];
	const mapped = source
		.map((option) => {
			if (!option || typeof option !== 'object') return null;
			const value = normalizeStatusValue(option.value ?? option.id);
			if (!value) return null;
			const labelValue = option.label ?? option.name ?? value;
			return { value, label: String(labelValue) };
		})
		.filter(Boolean);

	return mapped.length ? mapped : DEFAULT_STATUS_OPTIONS;
});

const paidStatus = computed(() => normalizeStatusValue(props.paidStatusValue) || 'paid');
const fallbackStatus = computed(() => normalizedOptions.value[0]?.value || 'unpaid');
const normalizeToKnownStatus = (value) => {
	const normalized = normalizeStatusValue(value);
	if (normalizedOptions.value.some((option) => option.value === normalized)) {
		return normalized;
	}
	return fallbackStatus.value;
};

const normalizedStatus = computed(() => normalizeToKnownStatus(props.status));
const statusLabel = computed(() => {
	const option = normalizedOptions.value.find((item) => item.value === normalizedStatus.value);
	return option?.label || normalizedStatus.value;
});
const badgeClass = computed(() =>
	`status-${normalizedStatus.value.replace(/[^a-z0-9_-]/g, '-')}`,
);

const formattedPaidDate = computed(() => {
	if (!props.paidDate) return '';
	const date = new Date(props.paidDate);
	if (Number.isNaN(date.getTime())) return props.paidDate;
	return date.toLocaleDateString();
});
const currentPaidDateInput = computed(() => normalizeDateInput(props.paidDate));
const showPaidDate = computed(() => normalizedStatus.value === paidStatus.value && !!formattedPaidDate.value);

const today = computed(() => new Date().toISOString().slice(0, 10));
const isPaidSelected = computed(() => selectedStatus.value === paidStatus.value);
const canConfirm = computed(() => {
	if (isPaidSelected.value) {
		return Boolean(paidDateInput.value) && paidDateInput.value <= today.value;
	}
	return true;
});
const showActions = computed(
	() =>
		isPaidSelected.value ||
		normalizedStatus.value === paidStatus.value ||
		selectedStatus.value !== normalizedStatus.value,
);

const isToggleMode = computed(() => {
	if (normalizedOptions.value.length !== 2) return false;
	return normalizedOptions.value.some((option) => option.value === paidStatus.value);
});
const nonPaidToggleValue = computed(() => {
	const option = normalizedOptions.value.find((item) => item.value !== paidStatus.value);
	return option?.value || fallbackStatus.value;
});
const isPaidToggled = computed({
	get: () => selectedStatus.value === paidStatus.value,
	set: (next) => {
		selectedStatus.value = next ? paidStatus.value : nonPaidToggleValue.value;
	},
});
const selectInputId = computed(() => `invoice-status-${String(props.itemId)}`);

const resetState = () => {
	selectedStatus.value = normalizedStatus.value;
	paidDateInput.value = normalizedStatus.value === paidStatus.value ? currentPaidDateInput.value : '';
	isEditorOpen.value = false;
};

resetState();

watch(
	() => [props.status, props.paidDate, props.statusOptions, props.paidStatusValue],
	() => {
		if (!isEditorOpen.value) {
			resetState();
		}
	},
);

const handleClickOutside = (event) => {
	if (!isEditorOpen.value) return;
	if (containerRef.value && !containerRef.value.contains(event.target) && !popoverRef.value?.contains(event.target)) {
		cancel();
	}
};

const handleExternalClose = (payload) => {
	if (payload?.exceptId === props.itemId) return;
	if (isEditorOpen.value) {
		cancel();
	}
};

let closeUnsub;

onMounted(() => {
	document.addEventListener('click', handleClickOutside, true);
	closeUnsub = onEvent('invoice-status-close-all', handleExternalClose);
});

onBeforeUnmount(() => {
	document.removeEventListener('click', handleClickOutside, true);
	if (closeUnsub) closeUnsub();
});

const handleBadgeClick = () => {
	if (props.disabled || props.loading) return;
	if (!isEditorOpen.value) {
		emitEvent('invoice-status-close-all', { exceptId: props.itemId });
	}
	toggleEditor();
};

const toggleEditor = () => {
	if (props.disabled || props.loading) return;
	if (isEditorOpen.value) {
		cancel();
		return;
	}

	selectedStatus.value = normalizedStatus.value;
	paidDateInput.value = normalizedStatus.value === paidStatus.value
		? (currentPaidDateInput.value || today.value)
		: '';
	isEditorOpen.value = true;
	emit('open');
};

const handleToggleChange = () => {
	if (!isPaidToggled.value) {
		paidDateInput.value = '';
	} else if (!paidDateInput.value) {
		paidDateInput.value = currentPaidDateInput.value || today.value;
	}
};

const handleStatusChange = () => {
	if (!isPaidSelected.value) {
		paidDateInput.value = '';
	} else if (!paidDateInput.value) {
		paidDateInput.value = currentPaidDateInput.value || today.value;
	}
};

const confirm = () => {
	if (!canConfirm.value || props.loading || props.disabled) return;
	const payload = {
		status: selectedStatus.value,
		paidDate: isPaidSelected.value ? paidDateInput.value : null,
	};
	emit('submit', payload);
	isEditorOpen.value = false;
	emit('close');
};

const cancel = () => {
	resetState();
	emit('close');
};

onDeactivated(() => { if (isEditorOpen.value) cancel(); });

defineExpose({
	openEditor: () => {
		if (!isEditorOpen.value) handleBadgeClick();
	},
});
</script>

<style lang="sass" scoped>
@use '@/assets/styles/variables.sass' as variables

.status-cell
	display: flex
	flex-direction: column
	align-items: stretch
	justify-content: center
	gap: 0px
	position: relative
	min-width: 100px
	height: 100%

.status-trigger
	display: flex
	flex-direction: column
	align-items: center
	justify-content: center
	gap: 2px
	width: 100%
	min-height: 40px
	padding: 3px 8px
	border: none
	background: transparent
	color: var(--text-color)
	font-family: inherit
	cursor: pointer
	touch-action: manipulation

	&:hover:not(:disabled), &:focus-visible
		background: var(--noc-background-secondary, #fafafa)
		outline: none

	&:disabled
		opacity: 0.5
		cursor: not-allowed

.status-badge
	display: inline-flex
	align-items: center
	justify-content: center
	padding: 4px 10px
	border-radius: 4px
	font-size: 0.75rem
	font-weight: 600
	line-height: 16px
	text-transform: uppercase
	border: 1px solid transparent
	cursor: pointer
	background-color: var(--noc-blue-10)
	color: var(--primary)
	font-family: inherit

	&.status-unpaid
		background-color: var(--noc-blue-10)
		color: var(--noc-blue)

	&.status-review
		background-color: #fef3c7
		color: #92400e

	&.status-approved
		background-color: #dbeafe
		color: #1d4ed8

	&.status-paid
		padding: 0
		border: 0
		background: transparent
		color: var(--text-color)

	&.status-rejected
		background-color: #fee2e2
		color: #b91c1c

.status-meta
	font-size: variables.$font-size-xsmall
	font-weight: 400
	color: var(--text-color-secondary)
	line-height: 18px
	white-space: nowrap

.status-popover
	box-sizing: border-box
	overflow-y: auto
	overscroll-behavior: contain
	position: absolute
	top: calc(100% + 6px)
	right: 0
	padding: 18px
	background-color: var(--noc-background)
	border-radius: variables.$border-radius
	box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12)
	min-width: 200px
	z-index: 9999
	display: flex
	flex-direction: column
	gap: 16px
	border: 1px solid var(--border-color)

	.toggle-row
		display: flex
		align-items: center
		justify-content: space-between
		gap: variables.$spacing-medium

	.status-select-row
		display: flex
		flex-direction: column
		gap: variables.$spacing-xsmall

	.status-select-input
		border: 1px solid var(--border-color)
		border-radius: variables.$border-radius
		padding: 6px 8px
		font-size: variables.$font-size-small
		background-color: var(--noc-background)
		color: var(--text-color)
		width: 100%
		box-sizing: border-box

.toggle-label
	font-size: variables.$font-size-small
	color: var(--text-color)
	font-weight: 500

.toggle-switch
	position: relative
	display: inline-block
	width: 44px
	height: 26px
	cursor: pointer

	input
		opacity: 0
		width: 0
		height: 0

		&:checked + .toggle-slider
			background-color: var(--primary)

		&:checked + .toggle-slider:before
			transform: translateX(18px)

	.toggle-slider
		position: absolute
		top: 0
		left: 0
		right: 0
		bottom: 0
		background-color: var(--border-color)
		border-radius: 26px
		transition: background-color 0.3s ease

		&:before
			content: ""
			position: absolute
			height: 22px
			width: 22px
			left: 2px
			bottom: 2px
			background-color: white
			border-radius: 50%
			transition: transform 0.3s ease
			box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2)

.date-picker
	display: flex
	flex-direction: column
	gap: variables.$spacing-xsmall

	:deep(.date-range-picker)
		width: 100%

	:deep(.drp-trigger)
		width: 100%
		min-height: 36px
		padding: 0 10px
		flex-direction: row-reverse
		justify-content: space-between
		border: 1px solid var(--border-color)
		border-radius: variables.$border-radius
		background-color: var(--noc-background)
		color: var(--text-color)
		font: 400 14px/20px var(--font-family-base)
		box-shadow: none
		cursor: pointer

	:deep(.drp-trigger:hover:not(:disabled)), :deep(.drp-trigger:focus-visible)
		background: var(--noc-background-secondary, #fafafa)
		outline: none

	:deep(.drp-dropdown--single)
		width: 304px
		box-sizing: border-box
		overflow: auto
		overscroll-behavior: contain

	:deep(.drp-body)
		padding: 12px

	:deep(.drp-calendars), :deep(.drp-calendar)
		width: 100%
		min-width: 0

	:deep(.drp-footer)
		position: sticky
		bottom: 0
		flex-wrap: wrap
		gap: 8px
		padding: 12px

	:deep(.drp-footer-actions)
		margin-left: auto

.popover-actions
	display: flex
	justify-content: flex-end
	gap: variables.$spacing-small

.btn-confirm, .btn-cancel
	padding: 6px 12px
	border-radius: variables.$border-radius
	font-size: variables.$font-size-small
	font-family: inherit
	cursor: pointer
	border: none
	transition: all 0.2s ease

.btn-confirm
	background-color: var(--primary)
	color: white

	&:disabled
		opacity: 0.5
		cursor: not-allowed

.btn-cancel
	background-color: transparent
	color: var(--text-color-secondary)

	&:hover
		background-color: var(--noc-blue-10)

.status-popover-enter-active,
.status-popover-leave-active
	transition: opacity 0.15s ease

.status-popover-enter-from,
.status-popover-leave-to
	opacity: 0

.status-cell.is-open
	z-index: 9999

@media (any-pointer: coarse)
	.status-trigger, .btn-confirm, .btn-cancel
		touch-action: manipulation
	.date-picker :deep(.drp-trigger), .date-picker :deep(.drp-cell), .date-picker :deep(.drp-btn), .date-picker :deep(.drp-nav-btn), .date-picker :deep(.drp-month-title)
		touch-action: manipulation
</style>
